import type {MutableRefObject, ReactElement} from 'react';
import React, {useState, useEffect} from 'react';
import {ImageAndVideoRecorderStatus, RecorderMediaType} from '@Panels/record-image-and-video-panel/record-image-and-video.types';
import LoadingPanel from '@Panels/loading-panel/loading-panel';
import {VideoPlayback} from '@Panels/record-image-and-video-panel/components/video-playback';
import {CapturedImage} from '@Panels/record-image-and-video-panel/components/captured-image';
import {updatePanelSizeData} from '@Components/panel/panel-reducer';
import {LOADER_SIZE} from '@Components/circular-progress-loader';
import {useAppDispatch, useAppSelector} from '@/hooks';
import styles from './image-and-video-recorder-feed.module.scss';

interface ImageAndVideoRecorderProps {
  stream: MediaStream | null;
  videoRef: MutableRefObject<HTMLVideoElement | null>;
  duration: number;
  className?: string;
  isOnMobile?: boolean;
  panelId?: string;
  showIosPlaybackVideoPreloader: boolean;
  setShowIosPlaybackVideoPreloader?(show: boolean): void;
}

export function ImageAndVideoRecorderFeed({
  className = '',
  isOnMobile = false,
  setShowIosPlaybackVideoPreloader = undefined,
  ...props
}: ImageAndVideoRecorderProps): ReactElement | null {
  const dispatch = useAppDispatch();

  const recorderStatus = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.imageAndVideoRecorderStatus;
  });

  const mediaType = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.mediaType;
  });

  const mediaUrl = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.mediaUrl;
  });

  const selectedCamera = useAppSelector((state) => {
    return state.recordImageAndVideoPanel.selectedCamera;
  });

  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (recorderStatus !== ImageAndVideoRecorderStatus.LOADING_RECORDER && recorderStatus !== ImageAndVideoRecorderStatus.STOPPED) {
      if (props.videoRef.current && props.stream) {
        props.videoRef.current.srcObject = props.stream;
      }
    }
  }, [props.stream]);

  const getLiveStreamToDisplay = (): ReactElement => {
    return (
      <div className={`${isOnMobile ? '_full-height _full-width' : `flex-center ${styles.videoFrame}`} `}>
        <video
          key={props.stream?.id ?? ''}
          onLoadedMetadata={(metadata) => {
            setWidth(metadata.currentTarget.clientWidth);
            dispatch(
              updatePanelSizeData({
                sizeDataId: props.panelId ?? '',
                sizeData: {
                  width: `${metadata.currentTarget.clientWidth + 96}px`,
                },
              })
            );
          }}
          height="420"
          id="stream"
          ref={props.videoRef}
          autoPlay
          playsInline
          muted
          className={`${isOnMobile ? '_full-width' : 'radius-4'}  ${styles.liveVideoFeed} ${isOnMobile ? '_full-height' : ''} ${selectedCamera !== 'environment' ? styles.mirror : ''}`}
        />
      </div>
    );
  };

  const getCapturedImageToDisplay = (): ReactElement => {
    return <CapturedImage imageSrc={mediaUrl} isOnMobile={isOnMobile} />;
  };

  const getFrameForVideo = (): ReactElement | undefined => {
    if (recorderStatus === ImageAndVideoRecorderStatus.LOADING_RECORDER) {
      return undefined;
    }

    if (mediaUrl && recorderStatus === ImageAndVideoRecorderStatus.STOPPED) {
      return (
        <VideoPlayback
          setShowIosPlaybackVideoPreloader={setShowIosPlaybackVideoPreloader}
          showIosPlaybackVideoPreloader={props.showIosPlaybackVideoPreloader}
          width={width}
          isOnMobile={isOnMobile}
          duration={props.duration}
        />
      );
    }
    if (recorderStatus === ImageAndVideoRecorderStatus.READY_TO_RECORD || recorderStatus === ImageAndVideoRecorderStatus.RECORDING) {
      return getLiveStreamToDisplay();
    }

    return undefined;
  };

  const getFrameForImage = (): ReactElement | undefined => {
    if (recorderStatus === ImageAndVideoRecorderStatus.LOADING_RECORDER) {
      return undefined;
    }

    if (mediaUrl && recorderStatus === ImageAndVideoRecorderStatus.STOPPED) {
      return getCapturedImageToDisplay();
    }

    return getLiveStreamToDisplay();
  };

  const getFrame = (): ReactElement | undefined => {
    if (mediaType === RecorderMediaType.VIDEO) {
      return getFrameForVideo();
    }
    if (mediaType === RecorderMediaType.IMAGE) {
      return getFrameForImage();
    }
    return undefined;
  };

  const getContent = (): ReactElement => {
    if (recorderStatus === ImageAndVideoRecorderStatus.LOADING_RECORDER) {
      return (
        <div className={`_full-height ${className}`}>
          <LoadingPanel loaderSize={LOADER_SIZE.MEDIUM} />
        </div>
      );
    }
    return <div className={`${className} flex-column-align-center _full-width ${props.showIosPlaybackVideoPreloader ? '_full-height' : ''}`}>{getFrame()}</div>;
  };

  return getContent();
}
